import React, { useState, useEffect, useCallback } from 'react'
import { Box, Tabs, Tab, Alert } from '@mui/material'
import { apiService } from '../../../services/api'
import OrangeOpaskaKPIs from '../orange-opaska/OrangeOpaskaKPIs'
import OrangeOpaskaTrend from '../orange-opaska/OrangeOpaskaTrend'
import OrangeOpaskaRegionDistrict from '../orange-opaska/OrangeOpaskaRegionDistrict'
import OrangeOpaskaSalonTable from '../orange-opaska/OrangeOpaskaSalonTable'
import OrangeOpaskaLastDaySalon from '../orange-opaska/OrangeOpaskaLastDaySalon'
import OrangeLocatorDailyChart from './OrangeLocatorDailyChart'
import OrangeLocatorKAMHistory from './OrangeLocatorKAMHistory'
import OrangeLocatorRawData from './OrangeLocatorRawData'
import {
  ReportShell, HeaderButton, FilterBar, Icon,
} from '../../report-shell'

const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
]

const OrangeLocatorSales = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [activeTab, setActiveTab] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)

  const [kpisData, setKpisData] = useState(null)
  const [kpisLoading, setKpisLoading] = useState(false)
  const [kpisError, setKpisError] = useState(null)

  const [breakdownData, setBreakdownData] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [breakdownError, setBreakdownError] = useState(null)

  const [kamHistoryData, setKamHistoryData] = useState(null)
  const [kamHistoryLoading, setKamHistoryLoading] = useState(false)
  const [kamHistoryError, setKamHistoryError] = useState(null)

  const fetchKpis = useCallback(async () => {
    try { setKpisLoading(true); setKpisError(null)
      const r = await apiService.getOrangeLocatorKpis(); setKpisData(r.data)
    } catch (err) { setKpisError(`Błąd pobierania KPI: ${err.message}`) }
    finally { setKpisLoading(false) }
  }, [])

  const fetchBreakdown = useCallback(async () => {
    try { setBreakdownLoading(true); setBreakdownError(null)
      const r = await apiService.getOrangeLocatorSalesBreakdown(selectedMonth, selectedYear); setBreakdownData(r.data)
    } catch (err) { setBreakdownError(`Błąd pobierania danych: ${err.message}`) }
    finally { setBreakdownLoading(false) }
  }, [selectedMonth, selectedYear])

  const fetchKamHistory = useCallback(async () => {
    try { setKamHistoryLoading(true); setKamHistoryError(null)
      const r = await apiService.getOrangeLocatorKamHistory(selectedMonth, selectedYear); setKamHistoryData(r.data)
    } catch (err) { setKamHistoryError(`Błąd pobierania historii: ${err.message}`) }
    finally { setKamHistoryLoading(false) }
  }, [selectedMonth, selectedYear])

  useEffect(() => { fetchKpis() }, [fetchKpis])
  useEffect(() => { setBreakdownData(null); setKamHistoryData(null) }, [selectedMonth, selectedYear])
  useEffect(() => {
    if (activeTab >= 1 && activeTab <= 4 && !breakdownData && !breakdownLoading) fetchBreakdown()
    if (activeTab === 5 && !kamHistoryData && !kamHistoryLoading) fetchKamHistory()
    if (activeTab === 6 && !breakdownData && !breakdownLoading) fetchBreakdown()
  }, [activeTab, breakdownData, breakdownLoading, fetchBreakdown, kamHistoryData, kamHistoryLoading, fetchKamHistory])

  const exportExcel = async () => {
    try { setExporting(true); setExportError(null)
      const r = await apiService.getOrangeLocatorExport(selectedMonth, selectedYear)
      const url = window.URL.createObjectURL(new Blob([r.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Lokalizator_Orange_${monthNames[selectedMonth - 1]}_${selectedYear}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) { setExportError(`Błąd eksportu: ${err.message}`) }
    finally { setExporting(false) }
  }

  const years = []
  const currentYear = new Date().getFullYear()
  for (let y = currentYear; y >= currentYear - 2; y--) years.push(y)

  const periods = [
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: monthNames[selectedMonth - 1],
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
  ]

  return (
    <ReportShell
      title="Orange — Lokalizator · Sprzedaż"
      subtitle="Sprzedaż produktu Lokalizator Orange — agregaty i drill-down."
      breadcrumb={['KAM-SFA', 'Orange', 'Lokalizator', 'Sprzedaż']}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportExcel} disabled={exporting}>
          <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
        </HeaderButton>
      }
      filterBar={<FilterBar periods={periods} />}
    >
      {exportError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>{exportError}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Podsumowanie" />
          <Tab label="Sprzedaż dzienna" />
          <Tab label="Regiony i dystrykty" />
          <Tab label="Salony" />
          <Tab label="Ost. dzień per salon" />
          <Tab label="Per KAM" />
          <Tab label="Trend" />
          <Tab label="Dane surowe" />
        </Tabs>
      </Box>

      {activeTab === 0 && <OrangeOpaskaKPIs data={kpisData} loading={kpisLoading} error={kpisError} />}
      {activeTab === 1 && <OrangeLocatorDailyChart data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 2 && <OrangeOpaskaRegionDistrict data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 3 && <OrangeOpaskaSalonTable data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 4 && <OrangeOpaskaLastDaySalon data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 5 && <OrangeLocatorKAMHistory data={kamHistoryData} loading={kamHistoryLoading} error={kamHistoryError} />}
      {activeTab === 6 && <OrangeOpaskaTrend data={breakdownData} loading={breakdownLoading} error={breakdownError} />}
      {activeTab === 7 && <OrangeLocatorRawData />}
    </ReportShell>
  )
}

export default OrangeLocatorSales
